import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Type,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import { DialogCloseReason, DialogService } from '@sensika/sui/dialog';

import { DataGridComponent } from './data-grid.component';
import { GridBulkDialogData } from './grid-bulk-scope';

const CONFIG_DIALOG_WIDTH = '560px';
const REVIEW_DIALOG_WIDTH = '860px';

/** One menu entry; `run` is the host's `openXDialog()`. */
export interface GridBulkAction {
  icon: IconProp;
  labelKey: string;
  run: () => void;
}

/** A divider between groups of entries (WAP-1745). */
export interface GridBulkActionSeparator {
  separator: true;
}

export type GridBulkActionItem = GridBulkAction | GridBulkActionSeparator;

/** What a host adds to the wrapper's scope snapshot when it opens a bulk dialog. */
export interface GridBulkOpenOptions<TId, TContext, TMeta> {
  /** Out-of-grid request context (the quick search, the deep-link fields). */
  filterContext: TContext;
  /** The meta the dialog bodies need. */
  meta: TMeta;
  /** `getRowId` strings -> the explorer's id type (Sitemaps: `Number`); the string itself when absent. */
  mapId?: (id: string) => TId;
}

/**
 * The bulk-actions menu of a grid with checkbox selection (WAP-1731): the
 * toolbar trigger, the menu with its scope header (the ticked rows, else every
 * row the current filters return) and the host's `items`. The items are an
 * input, not projected content: `sui-menu-item` injects the roving-focus group
 * `sui-menu` provides, and a projected button resolves DI at its declaration
 * site in the host, outside the menu (NG0201). `open()` snapshots the scope
 * from the wrapper (`selectedIds()`, `filterModel()`, `totalCount()`, WAP-1739)
 * plus the host's context and meta, then runs the config -> review -> run
 * chain every bulk action shares: the config dialog closes with the prepared
 * plan on Confirm, the review dialog runs it, and a confirmed review refreshes
 * the grid. Projected into `[toolbarActions]`; reaches the grid through DI, so
 * the element must sit inside `<app-data-grid>`. Declared in DataGridModule.
 */
@Component({
  selector: 'app-grid-bulk-actions',
  templateUrl: './grid-bulk-actions.component.html',
  styleUrls: ['./grid-bulk-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GridBulkActionsComponent {
  protected readonly grid = inject(DataGridComponent);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);

  readonly items = input.required<GridBulkActionItem[]>();

  protected isSeparator(
    item: GridBulkActionItem,
  ): item is GridBulkActionSeparator {
    return 'separator' in item;
  }

  /** Opens the config dialog with the scope snapshot; its Confirm result feeds the review dialog. */
  open<TId, TContext, TMeta>(
    configDialog: Type<unknown>,
    reviewDialog: Type<unknown>,
    options: GridBulkOpenOptions<TId, TContext, TMeta>,
  ): void {
    const mapId = options.mapId ?? ((id: string) => id as unknown as TId);
    const data: GridBulkDialogData<TId, TContext> & { meta: TMeta } = {
      selectedIds: this.grid.selectedIds().map(mapId),
      filterContext: options.filterContext,
      filterModel: this.grid.filterModel() ?? {},
      totalCount: this.grid.totalCount(),
      meta: options.meta,
    };
    // The menu item keeps focus otherwise and the dialog's focus trap fights it.
    (document.activeElement as HTMLElement | null)?.blur();
    this.dialogService
      .openSimpleDialog(configDialog, {
        data,
        closeOnClick: false,
        width: CONFIG_DIALOG_WIDTH,
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event?.reason === DialogCloseReason.Confirm && event.data) {
          this.review(reviewDialog, event.data);
        }
      });
  }

  private review(reviewDialog: Type<unknown>, result: unknown): void {
    this.dialogService
      .openSimpleDialog(reviewDialog, {
        data: result,
        closeOnClick: false,
        width: REVIEW_DIALOG_WIDTH,
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event?.reason === DialogCloseReason.Confirm) {
          this.grid.refresh();
        }
      });
  }
}
